import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import ConstructionOutlinedIcon from "@mui/icons-material/ConstructionOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import DocumentScannerOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import FolderCopyOutlinedIcon from "@mui/icons-material/FolderCopyOutlined";
import FoundationOutlinedIcon from "@mui/icons-material/FoundationOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PlumbingOutlinedIcon from "@mui/icons-material/PlumbingOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import PropaneTankOutlinedIcon from "@mui/icons-material/PropaneTankOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";

/**
 * Accent hues for module badges - a darker shade for the white day theme and
 * a lighter one that reads as a glow on the navy night theme.
 */
const HUES = {
  blue: { light: "#2563eb", dark: "#60a5fa" },
  sky: { light: "#0284c7", dark: "#38bdf8" },
  indigo: { light: "#4f46e5", dark: "#818cf8" },
  violet: { light: "#7c3aed", dark: "#a78bfa" },
  purple: { light: "#9333ea", dark: "#c084fc" },
  fuchsia: { light: "#c026d3", dark: "#e879f9" },
  pink: { light: "#db2777", dark: "#f472b6" },
  orange: { light: "#ea580c", dark: "#fb923c" },
  amber: { light: "#d97706", dark: "#fbbf24" },
  lime: { light: "#65a30d", dark: "#a3e635" },
  green: { light: "#16a34a", dark: "#4ade80" },
  emerald: { light: "#059669", dark: "#34d399" },
  teal: { light: "#0d9488", dark: "#2dd4bf" },
  cyan: { light: "#0891b2", dark: "#22d3ee" },
  slate: { light: "#475569", dark: "#94a3b8" },
};

const visual = (icon, hue) => ({ icon, hue });

const PLANT = visual(FactoryOutlinedIcon, "blue");
const ESTIMATION = visual(CalculateOutlinedIcon, "indigo");
const PRESSURE_VESSEL = visual(PropaneTankOutlinedIcon, "violet");
const PIPING = visual(PlumbingOutlinedIcon, "teal");
const INSTRUMENTATION = visual(SpeedOutlinedIcon, "cyan");
const STRUCTURAL = visual(FoundationOutlinedIcon, "amber");
const STORAGE_TANK = visual(ViewInArOutlinedIcon, "sky");
const SPEC = visual(LibraryBooksOutlinedIcon, "lime");
const GA = visual(ArchitectureOutlinedIcon, "slate");
const PROCUREMENT = visual(ShoppingCartOutlinedIcon, "emerald");
const PMS = visual(Inventory2OutlinedIcon, "emerald");
const EMS = visual(DocumentScannerOutlinedIcon, "blue");
const CMS = visual(ConstructionOutlinedIcon, "orange");
const SCHEDULE = visual(CalendarMonthOutlinedIcon, "pink");
const MIS = visual(InsightsOutlinedIcon, "sky");
const VALIDATION = visual(VerifiedUserOutlinedIcon, "green");
const AIMS = visual(PrecisionManufacturingOutlinedIcon, "sky");
const CLIENT_DATA = visual(HandshakeOutlinedIcon, "teal");
const TASK = visual(AssignmentTurnedInOutlinedIcon, "amber");

const FALLBACK = visual(AppsOutlinedIcon, "slate");

/**
 * The icon and accent colour of every module, section and discipline the app
 * navigates to, keyed by the key each place already uses for it:
 * - tree sections / procurement categories (treeConstants.js),
 * - WBS discipline groups (PROJECT_DISCIPLINES keys),
 * - plant module tiles and launcher groups (plantModules.js),
 * - top-level nav items (navItems.js `visualKey`).
 * One place, so a module looks the same in the tree, the launcher and the
 * sidebar rail.
 */
const MODULE_VISUALS = {
  // Top level
  projects: visual(AccountTreeOutlinedIcon, "blue"),
  aims: AIMS,
  software: visual(DevicesOutlinedIcon, "indigo"),
  "client-data": CLIENT_DATA,
  licences: visual(KeyOutlinedIcon, "amber"),

  // Plant tree sections
  plant: PLANT,
  estimation: ESTIMATION,
  procurement: PROCUREMENT,
  "project-control": visual(TrackChangesOutlinedIcon, "purple"),
  dashboard: visual(DashboardOutlinedIcon, "pink"),
  validation: VALIDATION,
  "design-automation": visual(AutoFixHighOutlinedIcon, "fuchsia"),

  // WBS disciplines
  pv: PRESSURE_VESSEL,
  tank: STORAGE_TANK,
  pip: PIPING,
  ins: INSTRUMENTATION,
  struc: STRUCTURAL,
  spec: SPEC,
  ga: GA,
  other: FALLBACK,

  // Procurement registers
  mrq: visual(RequestQuoteOutlinedIcon, "emerald"),
  vendorBidEvaluation: visual(GavelOutlinedIcon, "orange"),
  po: visual(ReceiptLongOutlinedIcon, "green"),
  mrv: visual(LocalShippingOutlinedIcon, "lime"),
  srv: visual(HomeRepairServiceOutlinedIcon, "amber"),

  // Plant modules (PLANT_MODULES keys)
  task: TASK,
  docRegister: visual(FolderCopyOutlinedIcon, "sky"),
  dcs: EMS,
  mcs: PMS,
  pcs: CMS,
  cms: visual(ChangeCircleOutlinedIcon, "fuchsia"),
  pms: visual(EventNoteOutlinedIcon, "purple"),
  mis: MIS,

  // Launcher entries (PLANT_MODULE_GROUPS keys)
  "estimation-structure": STRUCTURAL,
  "estimation-piping": PIPING,
  "estimation-instrumentation": INSTRUMENTATION,
  "estimation-pv": PRESSURE_VESSEL,
  "mcs-equipment-list": PMS,
  "mcs-valves-instrumentation": INSTRUMENTATION,
  "mcs-bulk-materials": PMS,
  "dcs-doc-reviewer": EMS,
  "mcs-4d-schedule": SCHEDULE,
  "dcs-4d-schedule": SCHEDULE,
  "pcs-4d-schedule": SCHEDULE,
  "pcs-fabrication": CMS,
  "pcs-itp": visual(AssignmentTurnedInOutlinedIcon, "pink"),

  // Module tabs (NavTabs `moduleKey`)
  schedule: SCHEDULE,
  "doc-transmittal": visual(SendOutlinedIcon, "sky"),
  "mis-progress": visual(TrendingUpOutlinedIcon, "green"),
  "mis-invoices": visual(ReceiptLongOutlinedIcon, "sky"),
  "mis-payables": visual(PaymentsOutlinedIcon, "amber"),
  "mis-manhours": visual(GroupsOutlinedIcon, "indigo"),
  "mis-profit-loss": visual(AccountBalanceOutlinedIcon, "emerald"),
  "mis-deviation": visual(CompareArrowsOutlinedIcon, "orange"),
};

export const getModuleVisual = (moduleKey) =>
  MODULE_VISUALS[moduleKey] ?? FALLBACK;

/** The module's accent colour for the given palette mode ("light"/"dark"). */
export const moduleAccentColor = (moduleKey, mode = "light") => {
  const hue = HUES[getModuleVisual(moduleKey).hue] ?? HUES.slate;
  return mode === "dark" ? hue.dark : hue.light;
};
